import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import './ResetPassword.css';
import logoFM from '../../assets/logo_trucktronix.png';

const ResetPassword = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [orgCode, setOrgCode] = useState('');
  const [loading, setLoading] = useState(false);

  const canSubmit = useMemo(() => {
    if (loading) return false;
    if (!email.trim()) return false;
    if (!orgCode.trim()) return false;
    return true;
  }, [email, orgCode, loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setLoading(true);

    try {
      const server = import.meta.env.VITE_API_SERVER;

      const response = await fetch(`${server}/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          organization_code: orgCode.trim(),
        }),
      });

      if (response.ok) {
        toast.success('Reset email sent!');
        // optional: back to login after success
        setTimeout(() => navigate('/login'), 700);
        return;
      }

      if (response.status === 400) {
        toast.error('Invalid user data.');
      } else if (response.status === 404) {
        toast.error('User not found.');
      } else {
        const msg = await response.text().catch(() => '');
        toast.error(`An error occurred during password reset.${msg ? ` ${msg}` : ''}`);
      }
    } catch (error) {
      toast.error(`An unexpected error occurred: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-page">
      <header className="reset-header">
        <button
          type="button"
          className="reset-btn reset-btn-ghost"
          onClick={() => navigate('/login')}
          disabled={loading}
          title="Back"
        >
          <i className="bi bi-arrow-left" aria-hidden="true" />
          Back
        </button>

        <div className="reset-header-right">
          <div className="reset-header-title">
            <div className="reset-header-title-main">Forgot Password</div>
            <div className="reset-header-title-sub">Send a reset email</div>
          </div>
          <img src={logoFM} alt="Fleet Master" className="reset-logo" />
        </div>
      </header>

      <main className="reset-main">
        <div className="reset-card" aria-busy={loading ? 'true' : 'false'}>
          <div className="reset-card-topbar">
            <div className="reset-chip">
              <i className="bi bi-envelope-paper" aria-hidden="true" />
              Password reset
            </div>

            {loading ? (
              <div className="reset-status">
                <span className="reset-spinner" aria-hidden="true" />
                Sending…
              </div>
            ) : (
              <div className="reset-status is-idle">
                <i className="bi bi-check2" aria-hidden="true" />
                Ready
              </div>
            )}
          </div>

          <form className="reset-form" onSubmit={handleSubmit} id="resetAccount">
            <div className="reset-form-head">
              <div className="reset-form-title">Request a reset link</div>
              <div className="reset-form-sub">
                Enter your account email and organization ID.
              </div>
            </div>

            <div className="reset-grid">
              <div className="reset-field">
                <div className="reset-label">Email</div>
                <input
                  type="email"
                  className="reset-input"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  autoComplete="email"
                  required
                />
              </div>

              <div className="reset-field">
                <div className="reset-label">Organization Code</div>
                <input
                  type="text"
                  className="reset-input"
                  placeholder="e.g. TT-001"
                  value={orgCode}
                  onChange={(e) => setOrgCode(e.target.value)}
                  disabled={loading}
                  autoComplete="organization"
                  required
                />
                <div className="reset-hint">
                  If you’re unsure, contact your administrator.
                </div>
              </div>
            </div>

            <div className="reset-actions">
              <button
                className="reset-btn reset-btn-primary"
                type="submit"
                disabled={!canSubmit}
                title={!canSubmit ? 'Fill all fields to continue' : undefined}
              >
                <i className="bi bi-send" aria-hidden="true" />
                <span>{loading ? 'Resetting…' : 'Send reset email'}</span>
              </button>
            </div>

            <div className="reset-footnote">
              For security, we won’t confirm whether an email exists beyond the status returned.
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default ResetPassword;
